import FontAwesome from '@expo/vector-icons/FontAwesome'
import { type ComponentProps, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, Alert, Pressable, TextInput, View } from 'react-native'
import { Icon } from '@/components/ui/Icon'
import { Text } from '@/components/ui/Text'
import { useAuth } from '@/contexts/AuthContext'
import { useThemeColors } from '@/contexts/ThemeContext'
import {
  type ApiComment,
  type ApiPost,
  createComment,
  deleteComment,
  getComments,
  toggleCommentLike,
} from '@/lib/api'
import { getInitials, relativeTime } from './utils'

// ─── Corner Brackets (admin accent) ──────────────────────────────────────────
const CornerBrackets = ({ color, size = 10 }: { color: string; size?: number }) => (
  <>
    <View
      className="absolute top-0 left-0"
      style={{
        width: size,
        height: size,
        borderTopWidth: 2,
        borderLeftWidth: 2,
        borderColor: color,
      }}
    />
    <View
      className="absolute top-0 right-0"
      style={{
        width: size,
        height: size,
        borderTopWidth: 2,
        borderRightWidth: 2,
        borderColor: color,
      }}
    />
    <View
      className="absolute bottom-0 left-0"
      style={{
        width: size,
        height: size,
        borderBottomWidth: 2,
        borderLeftWidth: 2,
        borderColor: color,
      }}
    />
    <View
      className="absolute bottom-0 right-0"
      style={{
        width: size,
        height: size,
        borderBottomWidth: 2,
        borderRightWidth: 2,
        borderColor: color,
      }}
    />
  </>
)

// ─── Action Chip ──────────────────────────────────────────────────────────────
function ActionChip({
  icon,
  label,
  active,
  onPress,
  disabled,
  testID,
  color: _color,
  muted,
  primary,
}: {
  icon: ComponentProps<typeof FontAwesome>['name']
  label: string | number
  active: boolean
  onPress?: () => void
  disabled?: boolean
  testID?: string
  color: string
  muted: string
  primary: string
}) {
  const activeColor = active ? primary : muted
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      className="flex-row items-center gap-1.5 px-3 py-1.5"
      style={{
        borderWidth: 1,
        borderColor: active ? `${primary}50` : `${muted}20`,
        borderRadius: 3,
        backgroundColor: active ? `${primary}0e` : 'transparent',
      }}
    >
      <Icon name={icon} size={11} color={activeColor} />
      <Text className="text-xs font-black uppercase tracking-widest" style={{ color: activeColor }}>
        {label}
      </Text>
    </Pressable>
  )
}

// ─── Inline Comment Section ───────────────────────────────────────────────────
type InlineCommentSectionProps = {
  postId: string
  currentUserId: string
  isAdmin: boolean
}

// eslint-disable-next-line complexity
function InlineCommentSection({ postId, currentUserId, isAdmin }: InlineCommentSectionProps) {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const { user } = useAuth()
  const [comments, setComments] = useState<ApiComment[]>([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [likeOverrides, setLikeOverrides] = useState<
    Map<string, { count: number; liked: boolean }>
  >(new Map())
  const inputRef = useRef<TextInput>(null)

  useEffect(() => {
    getComments(postId)
      .then(setComments)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [postId])

  const myInitials = user?.name?.trim() ? getInitials(user.name) : '?'

  async function handleSubmit() {
    const trimmed = text.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    try {
      const created = await createComment(postId, trimmed)
      setComments((prev) => [...prev, created])
      setText('')
    } catch {
      Alert.alert(t('communityFeed.commentError'))
    } finally {
      setSubmitting(false)
    }
  }

  function handleDeleteComment(id: string) {
    if (window.confirm(t('communityFeed.confirmDelete'))) {
      const snapshot = comments
      setComments((prev) => prev.filter((c) => c.id !== id))
      deleteComment(id).catch(() => setComments(snapshot))
    }
  }

  function handleLikeComment(c: ApiComment) {
    const current = likeOverrides.get(c.id) ?? { count: c.like_count, liked: c.liked_by_me }
    const next = current.liked
      ? { count: current.count - 1, liked: false }
      : { count: current.count + 1, liked: true }
    setLikeOverrides((prev) => new Map(prev).set(c.id, next))
    toggleCommentLike(c.id).catch(() => {
      setLikeOverrides((prev) => new Map(prev).set(c.id, current))
    })
  }

  return (
    <View style={{ borderTopWidth: 1, borderTopColor: `${colors.border}60` }}>
      {/* Section label */}
      <View
        className="flex-row items-center gap-2 px-4 py-2"
        style={{ borderBottomWidth: 1, borderBottomColor: `${colors.border}40` }}
      >
        <View className="w-1 h-1 rounded-full" style={{ backgroundColor: colors.primary }} />
        <Text
          className="text-xs font-black uppercase tracking-[5px]"
          style={{ color: colors.muted }}
        >
          {t('communityFeed.echoChannel')}
        </Text>
      </View>

      {loading ? (
        <ActivityIndicator size="small" color={colors.primary} style={{ margin: 16 }} />
      ) : (
        <>
          {comments.length === 0 && (
            <Text
              className="text-xs font-black uppercase tracking-widest text-center py-5"
              style={{ color: `${colors.muted}60` }}
            >
              {t('communityFeed.noComments')}
            </Text>
          )}
          {comments.map(
            // eslint-disable-next-line complexity
            (c, index) => {
              const liked = (likeOverrides.get(c.id) ?? { liked: c.liked_by_me }).liked
              const count = (likeOverrides.get(c.id) ?? { count: c.like_count }).count
              const canDelete = c.user_id === currentUserId || isAdmin

              return (
                <View key={c.id}>
                  <View
                    className="pl-10 pr-4 py-3 gap-2"
                    style={
                      c.is_admin
                        ? {
                            backgroundColor: `${colors.primary}08`,
                            borderLeftWidth: 2,
                            borderLeftColor: colors.primary,
                          }
                        : undefined
                    }
                  >
                    {/* Comment header */}
                    <View className="flex-row items-center justify-between">
                      <View className="flex-row items-center gap-2">
                        {/* Rank tag instead of avatar */}
                        <View
                          className="px-1.5 py-0.5 items-center justify-center"
                          style={{
                            borderWidth: 1,
                            borderColor: c.is_admin ? colors.primary : `${colors.muted}35`,
                            borderRadius: 2,
                            backgroundColor: c.is_admin ? `${colors.primary}15` : 'transparent',
                          }}
                        >
                          <Text
                            className="font-black"
                            style={{
                              fontSize: 10,
                              color: c.is_admin ? colors.primary : colors.muted,
                              letterSpacing: 1,
                            }}
                          >
                            {c.is_admin ? 'ADMIN' : getInitials(c.author_name)}
                          </Text>
                        </View>

                        <Text className="text-xm font-medium" style={{ color: colors.dark }}>
                          {c.author_name}
                        </Text>
                        <Text className="text-xs" style={{ color: `${colors.muted}80` }}>
                          {relativeTime(c.created_at)}
                        </Text>
                      </View>

                      {canDelete && (
                        <Pressable
                          testID={`community-comment-delete-${c.id}`}
                          onPress={() => handleDeleteComment(c.id)}
                          hitSlop={8}
                        >
                          <Icon name="trash-o" size={18} color={`${colors.muted}60`} />
                        </Pressable>
                      )}
                    </View>

                    {/* Body */}
                    <Text className="text-sm leading-relaxed" style={{ color: colors.dark }}>
                      {c.body}
                    </Text>

                    {/* Action Bar */}
                    <View className="flex-row items-center gap-2 pt-2">
                      <ActionChip
                        icon={liked ? 'heart' : 'heart-o'}
                        label={count}
                        active={liked}
                        onPress={() => handleLikeComment(c)}
                        disabled={false}
                        testID={`community-comment-like-${c.id}`}
                        color={colors.dark}
                        muted={colors.muted}
                        primary={colors.primary}
                      />
                    </View>
                  </View>

                  {/* Thin separator between comments */}
                  {index < comments.length - 1 && (
                    <View
                      style={{
                        height: 1,
                        backgroundColor: `${colors.border}30`,
                        marginLeft: 40,
                        marginRight: 16,
                      }}
                    />
                  )}
                </View>
              )
            }
          )}
        </>
      )}

      {/* Compose */}
      <View
        className="flex-row items-center gap-3 px-4 py-3"
        style={{ borderTopWidth: 1, borderTopColor: `${colors.border}40` }}
      >
        {/* Callsign badge */}
        <View
          className="px-1.5 py-0.5 items-center justify-center"
          style={{
            borderWidth: 1,
            borderColor: `${colors.primary}40`,
            borderRadius: 2,
            backgroundColor: `${colors.primary}10`,
          }}
        >
          <Text
            className="font-black"
            style={{ fontSize: 10, color: colors.primary, letterSpacing: 1 }}
          >
            {myInitials}
          </Text>
        </View>

        <TextInput
          testID={`community-comment-input-${postId}`}
          ref={inputRef}
          className="flex-1 text-sm"
          style={{ color: colors.dark, minHeight: 28 }}
          placeholder={t('communityFeed.commentPlaceholder')}
          placeholderTextColor={`${colors.muted}60`}
          value={text}
          onChangeText={setText}
          multiline
          maxLength={1000}
        />

        <Pressable
          testID={`community-comment-submit-${postId}`}
          onPress={handleSubmit}
          disabled={!text.trim() || submitting}
          className="px-3 py-1.5 items-center justify-center"
          style={{
            borderWidth: 1,
            borderColor: text.trim() && !submitting ? colors.primary : `${colors.muted}25`,
            borderRadius: 3,
            backgroundColor: text.trim() && !submitting ? `${colors.primary}15` : 'transparent',
          }}
        >
          <Text
            className="text-xs font-black uppercase tracking-widest"
            style={{ color: text.trim() && !submitting ? colors.primary : `${colors.muted}50` }}
          >
            {t('communityFeed.send')}
          </Text>
        </Pressable>
      </View>
    </View>
  )
}

// ─── Post Card ────────────────────────────────────────────────────────────────
type Props = {
  post: ApiPost & { isPending?: boolean }
  currentUserId: string
  onDelete: (id: string) => void
  onLike: (postId: string) => void
  onPin?: (postId: string, pinned: boolean) => void
}

// eslint-disable-next-line complexity
export function PostCard({ post, currentUserId, onDelete, onLike, onPin }: Props) {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const { isAdmin } = useAuth()
  const [commentsOpen, setCommentsOpen] = useState(false)
  const isOwn = post.user_id === currentUserId
  const canDeletePost = isOwn || isAdmin
  const initials = post.author_name?.trim() ? getInitials(post.author_name) : '?'

  function handleEllipsis() {
    if (window.confirm(t('communityFeed.confirmDelete'))) {
      onDelete(post.id)
    }
  }

  function handlePin() {
    if (onPin) {
      onPin(post.id, !post.is_pinned)
    }
  }

  return (
    <View
      testID={`community-post-${post.id}`}
      style={{
        borderWidth: 1,
        borderColor: post.is_admin ? `${colors.primary}35` : `${colors.border}60`,
        borderRadius: 3,
        backgroundColor: colors.surface,
        opacity: post.isPending ? 0.55 : 1,
        overflow: 'hidden',
      }}
    >
      {/* Admin corner brackets */}
      {post.is_admin && <CornerBrackets color={colors.primary} size={12} />}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <View
        className="px-4 pt-3 pb-2.5 flex-row items-center justify-between"
        style={
          post.is_admin
            ? {
                backgroundColor: `${colors.primary}08`,
                borderBottomWidth: 1,
                borderBottomColor: `${colors.primary}18`,
              }
            : { borderBottomWidth: 1, borderBottomColor: `${colors.border}35` }
        }
      >
        <View className="flex-row items-center gap-2.5">
          {/* Callsign box */}
          <View
            className="px-2 py-1 items-center justify-center"
            style={{
              borderWidth: 1,
              borderColor: post.is_admin ? colors.primary : `${colors.muted}35`,
              borderRadius: 2,
              backgroundColor: post.is_admin ? `${colors.primary}15` : `${colors.muted}08`,
            }}
          >
            <Text
              className="font-black"
              style={{
                fontSize: 10,
                color: post.is_admin ? colors.primary : colors.muted,
                letterSpacing: 1.5,
              }}
            >
              {post.is_admin ? 'ADMIN' : initials}
            </Text>
          </View>

          <Text className="text-sm font-black" style={{ color: colors.dark }}>
            {post.author_name || '...'}
          </Text>
          <Text
            className="text-xs font-medium uppercase tracking-widest"
            style={{ color: `${colors.muted}70` }}
          >
            {relativeTime(post.created_at)}
          </Text>
        </View>

        <View className="flex-row items-center gap-3">
          {post.is_pinned && (
            <View
              className="flex-row items-center gap-1 px-2 py-1"
              style={{
                borderWidth: 1,
                borderColor: `${colors.primary}50`,
                borderRadius: 2,
                backgroundColor: `${colors.primary}15`,
              }}
            >
              <Icon name="thumb-tack" size={10} color={colors.primary} />
              <Text
                className="text-[9px] font-black uppercase tracking-[3px]"
                style={{ color: colors.primary }}
              >
                {t('communityFeed.pinned')}
              </Text>
            </View>
          )}
          {post.isPending && (
            <View className="flex-row items-center gap-1">
              <ActivityIndicator size="small" color={colors.primary} />
              <Text
                className="text-xs font-black uppercase tracking-widest"
                style={{ color: colors.primary }}
              >
                {t('communityFeed.transmitting')}
              </Text>
            </View>
          )}
          {canDeletePost && !post.isPending && (
            <Pressable
              testID={`community-post-delete-${post.id}`}
              onPress={handleEllipsis}
              hitSlop={8}
            >
              <Icon name="trash" size={18} color={`${colors.muted}60`} />
            </Pressable>
          )}
          {isAdmin && onPin && !post.isPending && (
            <Pressable testID={`community-post-pin-${post.id}`} onPress={handlePin} hitSlop={8}>
              <Icon
                name="thumb-tack"
                size={18}
                color={post.is_pinned ? colors.primary : `${colors.muted}60`}
              />
            </Pressable>
          )}
        </View>
      </View>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <View className="px-4 py-4">
        <Text
          className="text-sm leading-relaxed font-sans"
          style={{ color: colors.dark, lineHeight: 22 }}
        >
          {post.body}
        </Text>
      </View>

      {/* ── Action Bar ──────────────────────────────────────────────────── */}
      <View
        className="flex-row items-center gap-2 px-4 py-2.5"
        style={{ borderTopWidth: 1, borderTopColor: `${colors.border}35` }}
      >
        <ActionChip
          icon={post.liked_by_me ? 'heart' : 'heart-o'}
          label={post.like_count}
          active={post.liked_by_me}
          onPress={() => onLike(post.id)}
          disabled={!!post.isPending}
          testID={`community-post-like-${post.id}`}
          color={colors.dark}
          muted={colors.muted}
          primary={colors.primary}
        />

        <ActionChip
          icon={commentsOpen ? 'comment' : 'comment-o'}
          label={post.comment_count}
          active={commentsOpen}
          onPress={() => !post.isPending && setCommentsOpen((v) => !v)}
          disabled={!!post.isPending}
          testID={`community-post-comments-${post.id}`}
          color={colors.dark}
          muted={colors.muted}
          primary={colors.primary}
        />
      </View>

      {/* ── Inline Comments ─────────────────────────────────────────────── */}
      {commentsOpen && (
        <InlineCommentSection postId={post.id} currentUserId={currentUserId} isAdmin={isAdmin} />
      )}
    </View>
  )
}
